import { useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { captureException } from '@sentry/browser';

import { SessionStorageService } from '@ion/user';

import { parseTokenInfo } from './parseTokenInfo';

/**
 * Hook to manage token refresh from Auth0.
 * Accepts getToken and setToken functions from useTokenRef.
 *
 * @param {Object} options - { setToken }
 * @returns {Object} - { refreshToken, refreshTokenAsync }
 */
export const useRefreshTokenAuth = ({ setToken }) => {
  const { getIdTokenClaims, getAccessTokenSilently } = useAuth0();
  const refreshPromiseRef = useRef(null);

  const refreshToken = useCallback(async () => {
    if (refreshPromiseRef.current) {
      return refreshPromiseRef.current;
    }

    refreshPromiseRef.current = (async () => {
      try {
        const { currentOrgId } = SessionStorageService.getSessionOrgInfo();

        await getAccessTokenSilently({
          cacheMode: 'off',
          ...(currentOrgId ? { organization: currentOrgId } : {}),
        });

        const idToken = await getIdTokenClaims();
        const parsedTokenInfo = parseTokenInfo(idToken);
        const { currentOrg, isOrgOverride } = parsedTokenInfo;
        const tokenOrgId = currentOrg.id;

        SessionStorageService.setSessionOrgInfo({ orgId: tokenOrgId, isOverride: isOrgOverride });

        handleUnexpectedOrgChange({ currentOrgId, tokenOrgId, isOrgOverride });

        if (!idToken) {
          return null;
        }

        setToken(idToken.__raw);

        return idToken.__raw;
      } catch (error) {
        captureException(error);
        return null;
      } finally {
        refreshPromiseRef.current = null;
      }
    })();

    return refreshPromiseRef.current;
  }, [getIdTokenClaims, getAccessTokenSilently, setToken]);

  const refreshTokenAsync = useCallback(
    () => refreshToken().catch(err => console.error('Token refresh failed', err)),
    [refreshToken]
  );

  return {
    refreshToken,
    refreshTokenAsync,
  };
};

function handleUnexpectedOrgChange({ currentOrgId, tokenOrgId, isOrgOverride }) {
  if (currentOrgId && currentOrgId !== tokenOrgId) {
    const exceptionMessage = isOrgOverride
      ? "User's org has changed while override is enabled, app reload required"
      : "User's org has changed while override is disabled, app reload required";

    captureException(new Error(exceptionMessage));
    alert('We detected that your current organization has changed. This window needs to reload to continue.');

    window.location.reload();
  }
}
